import { useItemSet } from '@exam-management/common/item-set-provider';
import { useMemo } from 'react';

import { ItemSetQuestionPreviewDialog, QuestionPreviewNavigationFooter } from '../../components';
import { useQuestionPreviewNavigation } from '../../hooks';

interface ItemSetQuestionPreviewModalProps {
  open: boolean;
  onClose: () => void;
}

export default function ItemSetQuestionPreviewModal(
  props: Readonly<ItemSetQuestionPreviewModalProps>,
) {
  const { open, onClose } = props;

  const { itemSet } = useItemSet();

  const { itemSetId, subQuestions, totalQuestions, paragraph } = useMemo(
    () => ({
      itemSetId: itemSet.id,
      subQuestions: itemSet.questions ?? [],
      totalQuestions: itemSet.totalQuestions ?? 0,
      paragraph: itemSet.metadata?.paragraph ?? '',
    }),
    [itemSet],
  );

  const {
    currentQuestionOrder,
    goToNextQuestion,
    goToPreviousQuestion,
    resetCurrentQuestionOrder,
  } = useQuestionPreviewNavigation(totalQuestions);

  const currentSubQuestion = useMemo<{
    id: string;
    details: string;
    choices: { label: string; order: number; isCorrect: boolean }[];
  }>(() => {
    const currentQuestion = subQuestions[currentQuestionOrder - 1];
    return {
      id: currentQuestion.id,
      details: currentQuestion.details,
      choices: currentQuestion.metadata.choices.map((choice) => ({
        label: choice.label,
        order: choice.order,
        isCorrect: choice.value === currentQuestion.correctAnswer.value,
      })),
    };
  }, [subQuestions, currentQuestionOrder]);

  return (
    <ItemSetQuestionPreviewDialog
      open={open}
      onClose={onClose}
      itemSetId={itemSetId}
      paragraph={paragraph}
      question={currentSubQuestion.details}
      questionId={currentSubQuestion.id}
      choices={currentSubQuestion.choices}
      totalQuestions={totalQuestions}
      currentQuestionOrder={currentQuestionOrder}
      slotProps={{
        transition: { onExited: resetCurrentQuestionOrder },
      }}
      footer={
        <QuestionPreviewNavigationFooter
          onNextQuestion={goToNextQuestion}
          onBackQuestion={goToPreviousQuestion}
          totalQuestion={totalQuestions}
          currentQuestionOrder={currentQuestionOrder}
        />
      }
    />
  );
}
